/**
 * TourCursor — floating cursor ghost driven by the tour interaction runner.
 *
 * Renders an SVG cursor at the screen position dictated by the tour context's
 * `cursorState` ({x, y, visible, clicking}). Smooth CSS transitions handle
 * the glide. When `clicking` is true, a soft circular ripple expands once.
 *
 * Z-index sits ABOVE the spotlight dim (9998) but BELOW the narrator panel
 * (9999) and the shortcuts modal (10000) so it never blocks user controls.
 */
import React from 'react';
import { useTour } from '../../context/TourContext';

export default function TourCursor() {
  const t = useTour();
  if (!t.isActive || !t.cursorState?.visible) return null;
  const { x, y, clicking, label } = t.cursorState;

  return (
    <div
      data-testid="tour-cursor-layer"
      className="fixed inset-0 z-[9998] pointer-events-none"
      aria-hidden
    >
      <div
        data-testid="tour-cursor"
        style={{
          position: 'absolute',
          left: x,
          top: y,
          transform: 'translate3d(-2px, -2px, 0)',
          transition: 'left 1.1s cubic-bezier(0.22, 0.61, 0.36, 1), top 1.1s cubic-bezier(0.22, 0.61, 0.36, 1)',
          willChange: 'left, top',
        }}
      >
        {/* Click ripple */}
        {clicking && (
          <div
            key={`ripple-${x}-${y}`}
            style={{
              position: 'absolute',
              left: -22,
              top: -22,
              width: 44,
              height: 44,
              borderRadius: '50%',
              border: '2px solid rgba(56,189,248,0.95)',
              boxShadow: '0 0 24px rgba(56,189,248,0.55)',
              animation: 'tour-cursor-ripple 0.7s ease-out forwards',
              pointerEvents: 'none',
            }}
          />
        )}
        {/* Cursor SVG (Apple-style arrow with subtle glow) */}
        <svg
          width="22"
          height="26"
          viewBox="0 0 22 26"
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: 'drop-shadow(0 4px 10px rgba(15,23,42,0.45)) drop-shadow(0 0 6px rgba(56,189,248,0.4))' }}
        >
          <path
            d="M 1 1 L 1 19 L 6 14.5 L 9.5 22 L 12.5 20.5 L 9 13 L 16 13 Z"
            fill="#0f172a"
            stroke="#ffffff"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
        {/* Optional label tag next to the cursor (e.g. "click Approve") */}
        {label && (
          <div
            style={{
              position: 'absolute',
              left: 22,
              top: 2,
              whiteSpace: 'nowrap',
            }}
            className="px-2 py-0.5 rounded-md bg-sky-500 text-white text-[10.5px] font-semibold shadow-lg"
            data-testid="tour-cursor-label"
          >
            {label}
          </div>
        )}
      </div>
    </div>
  );
}
